import { styles } from './ui/styles.js';

export interface Shell {
  /** Wrapper inside the shadow root. Mount all widget UI here, not on the root. */
  root: HTMLElement;
  /** The host element in the light DOM; carries the exclude marker. */
  host: HTMLElement;
  setAccent(colour: string): void;
  destroy(): void;
}

/**
 * Creates the widget's isolated mount point.
 *
 * A *closed* shadow root is used deliberately: the host page cannot reach in via
 * `.shadowRoot` and accidentally restyle or query our internals, which keeps the
 * widget's behaviour identical across every project it is dropped into.
 *
 * The host element is tagged `data-fb-widget` — the same selector the capture
 * pipeline excludes, so the widget never photographs itself.
 */
export function createShell(): Shell {
  const host = document.createElement('div');
  host.setAttribute('data-fb-widget', '');
  /*
   * `all: initial` must be inline. Declarations in the outer page outrank :host
   * rules, so a stray `div { margin: 2rem }` on the host site would otherwise
   * reach this element — inline style is the only thing that reliably beats it.
   * The host box itself is inert; the UI inside is position:fixed.
   */
  host.style.cssText = 'all:initial;position:static;';

  const shadow = host.attachShadow({ mode: 'closed' });

  // Constructable stylesheets avoid injecting a <style> element, which keeps us
  // clear of `style-src` CSP restrictions that would need a nonce we cannot know.
  if ('adoptedStyleSheets' in Document.prototype && typeof CSSStyleSheet === 'function') {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(styles);
      shadow.adoptedStyleSheets = [sheet];
    } catch {
      appendStyleElement(shadow);
    }
  } else {
    appendStyleElement(shadow);
  }

  // Typography lives on this wrapper rather than :host, out of reach of the page.
  const root = document.createElement('div');
  root.className = 'fb-root';
  shadow.append(root);

  document.body.append(host);

  return {
    root,
    host,
    setAccent(colour: string) {
      // Applied to the host so it cascades into the shadow tree via :host.
      host.style.setProperty('--fb-accent', colour);
    },
    destroy() {
      host.remove();
    },
  };
}

function appendStyleElement(root: ShadowRoot): void {
  const style = document.createElement('style');
  style.textContent = styles;
  root.append(style);
}
